import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IPublicClientApplication } from '@azure/msal-browser';
import { TaskStatus, TaskPriority } from '../../types';
import { searchAllProjects, CrossSearchResults, SearchHit } from '../../services/searchService';
import { TabKey } from '../../views/project/ProjectNavBar';

interface Props { pca: IPublicClientApplication; onClose: () => void; projectId: string; projectName?: string }

const EMPTY: CrossSearchResults = { tasks: [], issues: [], decisions: [], milestones: [] };

const TASK_STATUS_BADGE: Record<number, { bg: string; fg: string; label: string }> = {
  [TaskStatus.Done]:       { bg: '#D1FAE5', fg: '#065F46', label: 'Done' },
  [TaskStatus.InProgress]: { bg: '#D4EEF5', fg: '#0A5568', label: 'In Progress' },
  [TaskStatus.InReview]:   { bg: '#FEF3C7', fg: '#92400E', label: 'In Review' },
  [TaskStatus.NotStarted]: { bg: '#F3F4F6', fg: '#5A6272', label: 'Not Started' },
  [TaskStatus.OnHold]:     { bg: '#FEE2E2', fg: '#991B1B', label: 'On Hold' },
  [TaskStatus.Cancelled]:  { bg: '#F3F4F6', fg: '#B8BEC9', label: 'Cancelled' },
};
const PRIORITY_BADGE: Record<number, { bg: string; fg: string; label: string }> = {
  [TaskPriority.Critical]: { bg: '#DC2626', fg: '#FFFFFF', label: 'Critical' },
  [TaskPriority.High]:     { bg: '#E59F1C', fg: '#1C1917', label: 'High' },
  [TaskPriority.Medium]:   { bg: '#2563EB', fg: '#FFFFFF', label: 'Medium' },
  [TaskPriority.Low]:      { bg: '#6B7280', fg: '#FFFFFF', label: 'Low' },
};

const TAB_FOR: Record<SearchHit['kind'], TabKey> = {
  task: 'tasks', issue: 'issues', decision: 'decisions', milestone: 'milestones',
};

export default function SearchPanel({ pca, onClose, projectId, projectName }: Props) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<CrossSearchResults>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  // Default scope = current project only. The toggle widens to all projects.
  const [allProjects, setAllProjects] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounce = useRef<number | null>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (debounce.current) window.clearTimeout(debounce.current);
    const query = q.trim();
    if (query.length < 2) { setResults(EMPTY); setLoading(false); setSearched(false); return; }
    setLoading(true);
    debounce.current = window.setTimeout(() => {
      searchAllProjects(pca, query, allProjects ? undefined : projectId)
        .then(r => { setResults(r); setLoading(false); setSearched(true); })
        .catch(() => { setResults(EMPTY); setLoading(false); setSearched(true); });
    }, 300);
    return () => { if (debounce.current) window.clearTimeout(debounce.current); };
  }, [q, pca, allProjects, projectId]);

  const go = (hit: SearchHit) => {
    if (!hit.projectId) return;
    onClose();
    // Carry focusKind so ProjectDetail opens the Task Detail Panel for a
    // task hit (same-project or cross-project). Navigating always pushes a
    // new history entry, so ProjectDetail's location-keyed effect fires
    // even when the target is the project already on screen.
    navigate(`/project/${hit.projectId}`, { state: { tab: TAB_FOR[hit.kind], focusId: hit.id, focusKind: hit.kind } });
  };

  const groups: { key: string; label: string; items: SearchHit[] }[] = [
    { key: 'tasks', label: 'Tasks', items: results.tasks },
    { key: 'issues', label: 'Issues', items: results.issues },
    { key: 'decisions', label: 'Decisions', items: results.decisions },
    { key: 'milestones', label: 'Milestones', items: results.milestones },
  ];
  const total = groups.reduce((a, g) => a + g.items.length, 0);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.28)', zIndex: 300 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 600, maxWidth: '100vw',
        background: '#fff', borderLeft: '0.5px solid var(--border-light)',
        boxShadow: 'var(--shadow-panel)', zIndex: 301,
        display: 'flex', flexDirection: 'column', fontFamily: 'var(--font-ui)',
      }}>
        {/* Search input */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: 'var(--ink-4)', fontSize: 16 }}>🔍</span>
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
            placeholder={allProjects ? 'Search across all projects...' : 'Search this project...'}
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, fontFamily: 'var(--font-ui)', color: 'var(--ink)', background: 'transparent' }} />
          {q && (
            <button type="button" onClick={() => { setQ(''); inputRef.current?.focus(); }} aria-label="Clear"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 18 }}>×</button>
          )}
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', fontSize: 18, marginLeft: 4 }}>Esc</button>
        </div>

        {/* Scope selector — default is the current project; toggle to all. */}
        <div style={{ padding: '8px 20px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface-2)' }}>
          <ScopeChip active={!allProjects} onClick={() => setAllProjects(false)}>
            {projectName ? `This project · ${projectName}` : 'This project'}
          </ScopeChip>
          <ScopeChip active={allProjects} onClick={() => setAllProjects(true)}>All Projects</ScopeChip>
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {q.trim().length < 2 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-4)', fontSize: 14 }}>
              Type at least 2 characters to search {allProjects ? 'across all your projects' : 'this project'}.
            </div>
          ) : loading ? (
            <Skeleton />
          ) : searched && total === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-4)', fontSize: 14 }}>
              No results for "{q.trim()}"
            </div>
          ) : (
            groups.filter(g => g.items.length > 0).map(g => (
              <div key={g.key} style={{ marginBottom: 8 }}>
                <div style={{ padding: '8px 20px 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="eyebrow">{g.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>{g.items.length}</span>
                </div>
                {g.items.map(hit => (
                  <button key={hit.id} type="button" onClick={() => go(hit)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                      padding: '10px 20px', border: 'none', background: 'transparent', cursor: 'pointer',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hit.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>{hit.projectName ?? 'Unknown project'}</div>
                    </div>
                    <HitBadges hit={hit} />
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}

function ScopeChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        background: active ? 'var(--brand-mist)' : 'transparent',
        color: active ? 'var(--brand)' : 'var(--ink-3)',
        border: `1px solid ${active ? 'var(--brand)' : 'var(--border)'}`,
        borderRadius: 999, padding: '3px 12px',
        fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: active ? 500 : 400,
        cursor: 'pointer', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
      {children}
    </button>
  );
}

function HitBadges({ hit }: { hit: SearchHit }) {
  if (hit.kind === 'task') {
    const s = hit.status != null ? TASK_STATUS_BADGE[hit.status] : null;
    const p = hit.priority != null ? PRIORITY_BADGE[hit.priority] : null;
    return (
      <span style={{ display: 'inline-flex', gap: 4, flexShrink: 0 }}>
        {s && <Pill bg={s.bg} fg={s.fg}>{s.label}</Pill>}
        {p && <Pill bg={p.bg} fg={p.fg}>{p.label}</Pill>}
      </span>
    );
  }
  if (hit.kind === 'milestone' && hit.duedate) {
    return <Pill bg="#F3F4F6" fg="#5A6272">{new Date(hit.duedate).toLocaleDateString()}</Pill>;
  }
  return null;
}

function Pill({ bg, fg, children }: { bg: string; fg: string; children: React.ReactNode }) {
  return (
    <span style={{
      background: bg, color: fg, fontSize: 10, fontWeight: 600,
      padding: '2px 7px', borderRadius: 3, whiteSpace: 'nowrap', fontFamily: 'var(--font-ui)',
    }}>{children}</span>
  );
}

function Skeleton() {
  return (
    <div style={{ padding: '8px 20px' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' }}>
          <div style={{ flex: 1 }}>
            <div style={{ height: 12, width: '60%', background: 'var(--surface-2)', borderRadius: 4, marginBottom: 6 }} />
            <div style={{ height: 10, width: '35%', background: 'var(--surface-2)', borderRadius: 4 }} />
          </div>
          <div style={{ height: 16, width: 60, background: 'var(--surface-2)', borderRadius: 3 }} />
        </div>
      ))}
    </div>
  );
}
